import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogModule,
  DialogRef, DialogService,
  KENDO_DIALOGS
} from '@progress/kendo-angular-dialog';
import {
  EvaluationAdditionalQuestion, EvaluationAdditionalQuestionNew,
  EvaluationAdditionalQuestionType,
  EvaluationType,
  HelpInfo, QuestionOption
} from '../../models/models.lib';
import { CasiCloseOptions, FormModes, RESPONSE_TYPES } from '../../common/constants.lib';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoggerService } from '../../shared/logger.service';
import { HelpInfoService } from '../../admin/help-info.service';
import { DomSanitizer } from '@angular/platform-browser';
import { setTitle, truncateHtml } from '../../common/utils.lib';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule, RadioButtonModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { Observable } from 'rxjs';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import {
  QuestionOptionComponent
} from '../../common-components/question-option/question-option.component/question-option.component';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { IntegerOnlyDirective } from '../../directives/integer-only';

@Component({
  selector: 'app-evaluation-additional-question-add-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    RadioButtonModule,
    KENDO_DIALOGS,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
    IntegerOnlyDirective
  ],
  templateUrl: './evaluation-additional-question-add-win.html',
  styleUrl: './evaluation-additional-question-add-win.scss'
})
export class EvaluationAdditionalQuestionAddWin implements OnInit {

  private _evaluation: EvaluationType | undefined;

  @Input() title: string = 'Title Not Set';


  @Input({ required: true }) set evaluation(val: EvaluationType) {
    this._evaluation = val;
    this.editForm.patchValue(this._evaluation);
  }
  get evaluation(): EvaluationType | undefined {
    return this._evaluation;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
  }
  get mode(): FormModes {
    return this._mode;
  };

  @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  editForm: FormGroup;
  helpInfoList: HelpInfo[] = [];

  public view: Observable<QuestionOption[]> = new Observable<QuestionOption[]>();
  public rawData: QuestionOption[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: QuestionOption[] = filterBy(this.rawData, this.filter);

  public currentItem: QuestionOption = QuestionOption.create({});
  public formMode: FormModes = FormModes.NOTSET;
  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public sort: SortDescriptor[] = [
    { field: "sort_order", dir: "asc" },
  ];

  // responseTypes: string[] = [
  //   'Text',
  //   'Numeric',
  //   'Select One',
  //   'Select Many'
  // ];

  responseTypes: string[] = RESPONSE_TYPES;

  JSON = JSON;

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private dataService: HelpInfoService,
    private sanitizer: DomSanitizer,
    private dialogService: DialogService
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        question: [null, [Validators.required]],
        response_type: ["Text", []],
        decimal_places: [null, []],
        options: [[], []],
        compulsory: [true, []],
        supervisor_applicable: [true, []],
        supervisee_applicable: [false, []],
        professional_applicable: [false, []],
        help_info_id: [null, []],
        evaluation_id: [null, [Validators.required]]
      }
    );
  }

  //#region Form

  get f() { return this.editForm.controls; }
  get question() { return this.editForm.get('question'); }
  get response_type() { return this.editForm.get('response_type'); }
  get decimal_places() { return this.editForm.get('decimal_places'); }
  get options() { return this.editForm.get('options'); }
  get compulsory() { return this.editForm.get('compulsory'); }
  get supervisor_applicable() { return this.editForm.get('supervisor_applicable'); }
  get supervisee_applicable() { return this.editForm.get('supervisee_applicable'); }
  get professional_applicable() { return this.editForm.get('professional_applicable'); }
  get help_info_id() { return this.editForm.get('help_info_id'); }

  //#endregion

  //#region Ng handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.loadHelpInfo();
    // Subscribe to response_type changes
    this.response_type?.valueChanges.subscribe(value => {
      this.setDecimalPlacesValidator(value);
    });

    // Apply initial validators in case default is Numeric
    this.setDecimalPlacesValidator(this.response_type?.value);

    this.rawData = this.editForm.value.options || [];// [{"sort_order":1,"option":"test1"},{"sort_order":2,"option":"test2"},{"sort_order":3,"option":"test3"},{"sort_order":4,"option":"test4"}];
    this.loadData();
  }

  //#endregion

  //#region Grid methods

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]).filter(v => v !== null && v !== undefined && v !== '');
  }

  //#endregion

  //#region Public methods

  getTruncatedHtml(html: string) {
    return truncateHtml(html, this.sanitizer, 50);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  public onSaveAction(): void {
    this.editForm.patchValue({
      options: this.rawData
    });
    this.logger.debug(`onSaveAction...`, this.editForm.value);
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.onSubmit.emit(this.editForm);

    // this.dialog.close({text: CasiCloseOptions.Saved, data: this.editForm.value});
  }

  public onRadioChange(): void {
    this.rawData = [];
    this.loadData();
  }

  public onUpClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.rawData = this.moveItem(this.rawData, dataItem.sort_order, "up");
    this.loadData();
  }

  public onDownClick(dataItem: any): void {
    this.logger.debug("onUpClick: ", dataItem);
    this.rawData = this.moveItem(this.rawData, dataItem.sort_order, "down");
    this.loadData();
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = QuestionOption.create(dataItem);
    this.openConfirmDailog(this.currentItem);
  }

  public onAddClick(): void {
    this.logger.debug("onAddClick...");
    this.currentItem = QuestionOption.create(this.evaluation);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = QuestionOption.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  //#endregion

  //#region Private methods

  private openAddEditDialog(mode: FormModes, dataItem: QuestionOption): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: QuestionOptionComponent,
      minWidth: 320,
      width: 600,
    });

    const contentRef = dialogRef.content.instance as QuestionOptionComponent;
    contentRef.title = setTitle(mode, 'QUESTION OPTION');
    contentRef.questionOption = dataItem;
    contentRef.mode = mode;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
        this.handleClose(contentRef.editForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data, mode);
    this.save(data, mode);
  }

  private save(option: any, mode: FormModes): void {
    this.logger.debug('save...', option, mode);
    if (mode === FormModes.EDIT) {
      let current = this.rawData.filter(item => item.sort_order === option.sort_order)[0];
      current.option = option.option;
      this.loadData();
    } else {
      option.sort_order = this.nextSortOrder(this.rawData);
      this.rawData.push(option);
      this.loadData();
    }
  }

  private openConfirmDailog(dataItem: any): void {
    this.logger.debug("openConfirmDailog: ", dataItem);
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450,
      // cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Option - <b>${this.currentItem.option}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.handleRemove(this.currentItem);
      }
    });
  }

  private handleRemove(args: any): void {
    this.logger.debug("handleRemove: ", args);
    this.rawData = this.deleteAndRecalculate(this.rawData, args.sort_order);
    this.loadData();
  }

  private deleteAndRecalculate(arr: QuestionOption[], sortOrderToDelete: number): QuestionOption[] {
    return arr
      .filter(item => item.sort_order !== sortOrderToDelete)
      .sort((a: any, b: any) => a.sort_order - b.sort_order)
      .map((item, index) => ({
        ...item,
        sort_order: index + 1
      }));
  }

  private nextSortOrder(arr: QuestionOption[]): number {
    if (!arr.length) return 1;
    return Math.max(...arr.map((x: any) => x.sort_order)) + 1;
  }

  private moveItem(arr: QuestionOption[], sortOrder: number, direction: string): QuestionOption[] {
    const itemIndex = sortOrder - 1; //sortOrder starts from1 not from zero
    const targetIndex = direction === "up" ? itemIndex - 1 : itemIndex + 1;

    const targetSortOrder = arr[targetIndex].sort_order;
    arr[itemIndex].sort_order = targetSortOrder;
    arr[targetIndex].sort_order = sortOrder;

    return arr.sort((a: any, b: any) => a.sort_order - b.sort_order);
  }

  private loadHelpInfo(): void {
    this.dataService.get().subscribe({
      next: data => this.helpInfoList = data || [],
      error: err => console.error('Failed to load list', err)
    });
  }

  private setDecimalPlacesValidator(value: string | null): void {
    if (value === 'Numeric') {
      this.decimal_places?.setValidators([Validators.required]);
      // this.decimal_places?.setValidators([Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]);
    } else {
      this.decimal_places?.clearValidators();
      this.decimal_places?.setValue(null); // reset when hidden
    }
    this.decimal_places?.updateValueAndValidity();
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}

